import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '@sdb-develop/learning-composition-player/src/store/store.js';
import { colors } from '@sdb-develop/learning-frontend/dist/src/sdb-colors.js';
import { translate } from '@sdb-develop/learning-composition-player/src/helpers/translate.js';
import { applyStyles } from '@sdb-develop/learning-composition-player/src/helpers/stylesheets.js';
import '@sdb-develop/learning-frontend';
import './shared/sdb-sliding-panel.js';
import '@sdb-develop/learning-composition-player/src/components/player/sdb-embedded-composition-player.js';
import '@sdb-develop/learning-composition-player/src/components/elearning/sdb-embedded-elearning-dialog.js';
import './registration/sdb-registration-form.js';

const ANIMATION_KEY = 'sdb-new-profile-button-animation';
@customElement('sdb-player-app')
class SdbCompositionApp extends connect(store)(LitElement)
 {

    @property({ type: String })
    accessor compositionId;

    @property({ type: String })
    accessor sessionId;

    @property({ type: String })
    accessor type;

    @property({ type: Boolean })
    accessor preview;

    @property({ type: String })
    accessor portal = 'GLOBAL';

    @property({ type: Object })
    accessor registrationKeys = [];

    @property({ type: Boolean })
    accessor hideCompositionStatus;

    @property({ type: Boolean })
    accessor browse;

    @state()
    accessor composition = {};

    @state()
    accessor profilePanelOpened = false;

    @state()
    accessor showNewAnimation = false;

    stateChanged(state) {
        this.userName = state.oidc.user.profile.name;
        this.user = state.oidc.user;
        this.composition = state.composition.data || {};
    }

    static get styles() {
        return css`
            :host {
                display: flex;
            }

            header {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 60px;
                background: #fff;
                border-bottom: 1px solid #ddd;
                display: flex;
                align-items: center;
                justify-content: flex-start;  /* keep children lined up from left */
                z-index: 1000;
            }

            header .logo {
                margin: 0;
                width: 90px;
                height: 60px;
                background: #6FC0D8;
                color: #fff;
                display: flex;
                align-items: center;
                justify-content: center;
                border-bottom-right-radius: 16px;
            }

            header .logo img {
                height: 36px;
                filter: brightness(0) invert(1);
            }

            header .chip {
                margin-left: 1.2rem;
                background: orange;
                color: #fff;
                padding: 3px 15px 5px 15px;
                margin-left: 20px;
                border-radius: 12px;
                font-size: 1.0rem;
            }

            header .title {
                margin-left: 1.2rem;
                font-size: 1.1rem;
                white-space: nowrap;   /* optional: prevent wrapping */
            }

            header .profile-button-group {
                margin-left: auto;
                margin-right: 1.2rem;
                display: inline-flex;
                align-items: center;
                gap: 0.4rem;
                border-radius: 24px;
                padding: 0.5rem 0.8rem;
                background: rgba(111, 192, 216, 0.1);
                transition: background 0.2s ease, transform 0.2s ease;
            }

            header .profile-button-group:hover {
                cursor: pointer;
                background: rgba(111, 192, 216, 0.2);
                transform: var(--sdb-button-scale-factor, scale(1.05));
            }

            header .profile-button-group.new-animation {
                animation: pulse 2s infinite;
            }

            .profile-label {
                font-size: 0.95rem;
                color: #333;
                user-select: none;
            }

            sdb-layout-main {
                margin-top: 60px;      /* same as header height */
                flex: 1 1 auto;
                overflow-y: auto;      /* scrolls if content is too tall */
            }

            @keyframes pulse {
                0% { box-shadow: 0 0 0 0 rgba(255, 153, 0, 0.5); }
                70% { box-shadow: 0 0 0 10px rgba(255, 153, 0, 0); }
                100% { box-shadow: 0 0 0 0 rgba(255, 153, 0, 0); }
            }

            .user-icon {
                animation: pulse 2s infinite;
                border-radius: 50%;
            }
        `;
    }

    render() {
        if (this.error)
            return html`
                <style>
                    div {
                        margin: 20px;
                    }
                </style>
                <div>${this.error}</div>
            `;

        if (this.isLoading)
            return html`<div class="spinner"></div>`;

        document.title = this.composition?.title || '';

        return html`
            <header>
                <div class="logo">
                    <img src="/images/sdb.svg">
                </div>
                ${this.preview ? html`<div class="chip">preview</div>` : nothing}
                <div class="title">${this.composition.title}</div>
                ${!this.preview ? html`
                    <div
                        class="profile-button-group ${classMap({ 'new-animation': this.showNewAnimation })}"
                        @click=${(e) => {
                            this.profilePanelOpened = true;
                            this._hideAnimation(); // hide badge once clicked
                        }}>
                        <sdb-content-icon
                            icon="fal fa-user"
                            size="24">
                        </sdb-content-icon>
                        <span class="profile-label">${translate('btnProfileLabel')}</span>
                    </div>
                ` : nothing}
            </header>
            <sdb-layout-main>
                <sdb-embedded-composition-player
                    .settings=${{
                        portalIdentifier: this.portal,
                        env: window.ENVIRONMENT,
                        registrationKeys: this.registrationKeys,
                    }}
                    .user=${this.user}
                    compositionId=${this.compositionId}
                    sessionId=${this.sessionId}
                    .type=${this.type}
                    ?preview=${this.preview}
                    ?hideCompositionStatus=${this.hideCompositionStatus}
                    ?browse=${this.browse}>
                </sdb-embedded-composition-player>
            </sdb-layout-main>

            <sdb-sliding-panel
                width="400px"
                header=${this.userName}
                .opened=${this.profilePanelOpened}
                @panel-closed=${() => { this.profilePanelOpened = false; }}>
                <sdb-registration-form></sdb-registration-form>
            </sdb-sliding-panel>

            <sdb-embedded-elearning-dialog
                .user=${this.user}>
            </sdb-embedded-elearning-dialog>
        `;
    }

    firstUpdated() {
        applyStyles(colors, true);

        // --- pulse visibility logic ---
        const now = Date.now();
        const savedDate = localStorage.getItem(ANIMATION_KEY);

        if (!savedDate || (now - Number(savedDate)) > 7 * 24 * 60 * 60 * 1000) {
            // Show pulse if first time or 1 week passed
            this.showNewAnimation = true;
        }
    }


    _hideAnimation() {
        this.showNewAnimation = false;
        localStorage.setItem(ANIMATION_KEY, Date.now().toString());
    }
}
